/**
 * Shared building blocks.
 *
 * Every value comes from the `@theme` block in `styles.css`, reached through
 * Tailwind utilities. Nothing here carries a literal colour, radius or type
 * size — `bg-canvas` and `text-body-sm` are the same tokens the stylesheet
 * compiles from, so a component and a stylesheet cannot drift apart.
 *
 * The system is monochrome at the action layer: one near-black primary button
 * per screen, everything else outlined or quiet. Colour is reserved for state —
 * a Run that failed, a destination that is unreachable — so that when something
 * on a screen is coloured, it means something.
 *
 * Layout wrappers (`Stack`, `Row`, `Columns`) are deliberately absent. They
 * existed only to inject inline styles; a `flex flex-col gap-lg` at the call
 * site says the same thing where the reader is already looking, and Tailwind
 * can only see class names it finds written out in the source.
 */

import {
  forwardRef,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  AlertIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
  CloseIcon,
  InfoIcon,
} from "./icons";
import { PAGE_SIZES, pageSummary, typedPage } from "../pagination";

/** Joins class names, dropping the falsy ones. */
export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}

// --- Card ------------------------------------------------------------------

/**
 * The workhorse surface. A card with a `title` grows a header band separated by
 * a hairline, which is what lets `flush` content — a table — meet the card's
 * edges without the header running into it.
 */
export function Card({
  title,
  description,
  actions,
  children,
  flush = false,
  className,
  bodyClassName,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  flush?: boolean;
  className?: string;
  bodyClassName?: string;
}) {
  const hasHeader = Boolean(title || actions);

  return (
    <section
      className={cx(
        // `clip-to-radius` rather than `overflow-hidden`: the two clip
        // identically, and only the second makes the card a scroll container
        // that a sticky table heading inside it would be trapped by. The
        // stylesheet has the measurement.
        "clip-to-radius rounded-lg border border-hairline bg-canvas shadow-subtle",
        className,
      )}
    >
      {hasHeader && (
        <header
          className={cx(
            "flex items-start justify-between gap-md px-lg py-md",
            flush && "border-b border-hairline",
          )}
        >
          <div className="min-w-0">
            {title && <h2 className="m-0 font-text text-title-sm">{title}</h2>}
            {description && <p className="mb-0 mt-xxs text-body-sm text-muted">{description}</p>}
          </div>
          {/*
            `mb-xs` so a button is not sitting on the content below it.

            The header's padding was measured against a one-line title. A
            button is 32px against the title's 20, so where the actions are the
            tallest thing in the header they eat twelve of the sixteen pixels
            meant to separate the header from the body — the Source Profile
            card had 16px under its buttons against 31px in the card beneath
            it, which is where this came from.

            On the margin rather than on the header's own padding, because the
            header is `items-start`: the margin only makes the header taller
            when the actions are the tallest item in it, so it adds room in
            exactly the cards that were short of it and changes nothing in a
            card whose title and description already reach further down.
          */}
          {actions && <div className="mb-xs flex shrink-0 items-center gap-xs">{actions}</div>}
        </header>
      )}
      {children !== undefined && (
        <div className={cx(flush ? "p-0" : hasHeader ? "px-lg pb-lg pt-0" : "p-lg", bodyClassName)}>
          {children}
        </div>
      )}
    </section>
  );
}

// --- Typography ------------------------------------------------------------

/**
 * A heading inside a card body, below the card's own title. Inter, not the
 * display face — the boundary in `DESIGN.md` is strict, and a sub-heading at
 * 16px is not a display headline.
 */
export function SectionTitle({ children, className }: { children: ReactNode; className?: string }) {
  return <h3 className={cx("mb-sm mt-0 font-text text-title-sm text-ink", className)}>{children}</h3>;
}

export function Muted({
  children,
  tone = "text-muted",
  className,
}: {
  children: ReactNode;
  /** The colour utility to draw in. A prop rather than something layered on
   *  through `className`, because two colour utilities on one element resolve
   *  by stylesheet order rather than by which was written last. */
  tone?: string;
  className?: string;
}) {
  return <span className={cx("text-body-sm", tone, className)}>{children}</span>;
}

export function Label({ children }: { children: ReactNode }) {
  return <span className="block text-overline uppercase text-muted">{children}</span>;
}

/** A path, a URL, a storage key: anything the user did not write in prose. */
export function Code({
  children,
  className,
  title,
}: {
  children: ReactNode;
  className?: string;
  /** The whole value, where the caller caps how much of it is drawn. */
  title?: string;
}) {
  // `inline-block` so a caller's `max-w` and `truncate` can take effect; a
  // bare inline span ignores both.
  return (
    <span className={cx("code", className && "inline-block align-middle", className)} title={title}>
      {children}
    </span>
  );
}

export function KeyValue({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-xxs text-body-sm text-ink">{children}</div>
    </div>
  );
}

/** The metric row at the top of a screen. Value in the display face. */
export function StatTile({
  label,
  value,
  hint,
  tone = "neutral",
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: "neutral" | "danger" | "warning";
}) {
  return (
    <div className="rounded-lg border border-hairline bg-canvas p-lg shadow-subtle">
      <Label>{label}</Label>
      <div
        className={cx(
          "mb-0 mt-xs font-display text-display-sm",
          tone === "danger" ? "text-danger-ink" : tone === "warning" ? "text-warning-ink" : "text-ink",
        )}
      >
        {value}
      </div>
      {hint && <div className="mt-xxs text-caption text-muted-soft">{hint}</div>}
    </div>
  );
}

// --- Buttons ---------------------------------------------------------------

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "on-dark";

export function Button({
  children,
  variant = "secondary",
  size = "md",
  type = "button",
  icon,
  onClick,
  disabled,
  title,
  className,
  // A button that opens something has to be able to say so. Named explicitly
  // rather than reached by spreading the rest, because the props above are the
  // ones this component styles and a spread would quietly accept anything.
  "aria-expanded": ariaExpanded,
  // And a button whose only text is a glyph has to be able to say what it is.
  // `title` does not stand in for this: it is a fallback for a button with no
  // name of its own, and "+" is a name — so the `+` on every scored row read
  // as "+" to a screen reader while the code passed a sentence and this
  // component dropped it on the floor.
  "aria-label": ariaLabel,
}: {
  children?: ReactNode;
  variant?: ButtonVariant;
  size?: "md" | "sm";
  type?: "button" | "submit";
  icon?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
  className?: string;
  "aria-expanded"?: boolean;
  "aria-label"?: string;
}) {
  return (
    <button
      className={cx("btn", `btn-${variant}`, size === "sm" && "btn-sm", className)}
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-expanded={ariaExpanded}
      aria-label={ariaLabel}
    >
      {icon}
      {children}
    </button>
  );
}

/** A circular icon-only control. Always carries a label for assistive tech. */
export function IconButton({
  icon,
  label,
  onClick,
  className,
}: {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <button
      className={cx("icon-btn", className)}
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
    >
      {icon}
    </button>
  );
}

/**
 * The bar under a paged table: what is showing on the left, the page controls
 * in the middle, rows per page on the right. On a phone the three stack, the
 * controls first, rather than squeezing onto one line.
 *
 * The page box can be typed into: Enter or leaving the box goes there, Escape
 * or anything that is not a page number puts the current page back.
 */
export function Pager({
  page,
  pageSize,
  total,
  lastPage,
  busy = false,
  onPage,
  onPageSize,
}: {
  page: number;
  pageSize: number;
  total: number;
  lastPage: number;
  /** A page is loading; stepping further would outrun it. */
  busy?: boolean;
  onPage: (page: number) => void;
  onPageSize: (size: number) => void;
}) {
  const [typed, setTyped] = useState(String(page));
  // The page can change without the box — an arrow, a shrinking list — and the
  // box has to follow it.
  useEffect(() => setTyped(String(page)), [page]);

  const go = () => {
    const target = typedPage(typed, lastPage);
    if (target === null || target === page) setTyped(String(page));
    else onPage(target);
  };

  const atStart = page <= 1 || busy;
  const atEnd = page >= lastPage || busy;

  return (
    <div className="flex flex-col items-center gap-sm border-t border-hairline px-lg py-sm fold:grid fold:grid-cols-[1fr_auto_1fr]">
      <span className="order-2 text-body-sm text-muted fold:order-none fold:justify-self-start">
        {pageSummary(page, pageSize, total)}
      </span>

      <nav className="order-1 flex items-center gap-xxs fold:order-none" aria-label="Pages">
        <button type="button" className="pager-arrow" aria-label="First page" disabled={atStart} onClick={() => onPage(1)}>
          <ChevronsLeftIcon size={18} />
        </button>
        <button type="button" className="pager-arrow" aria-label="Previous page" disabled={atStart} onClick={() => onPage(page - 1)}>
          <ChevronLeftIcon size={18} />
        </button>
        <input
          className="pager-page mx-xs"
          inputMode="numeric"
          aria-label={`Page, of ${lastPage}`}
          value={typed}
          disabled={busy}
          onChange={(event) => setTyped(event.target.value)}
          onBlur={go}
          onKeyDown={(event) => {
            if (event.key === "Enter") go();
            if (event.key === "Escape") setTyped(String(page));
          }}
        />
        <span className="mr-xs text-body-sm text-muted">of {lastPage}</span>
        <button type="button" className="pager-arrow" aria-label="Next page" disabled={atEnd} onClick={() => onPage(page + 1)}>
          <ChevronRightIcon size={18} />
        </button>
        <button type="button" className="pager-arrow" aria-label="Last page" disabled={atEnd} onClick={() => onPage(lastPage)}>
          <ChevronsRightIcon size={18} />
        </button>
      </nav>

      <label className="order-3 flex items-center gap-xs text-body-sm text-muted fold:order-none fold:justify-self-end">
        Rows per page:
        <select
          className="pager-select"
          value={String(pageSize)}
          onChange={(event) => onPageSize(Number(event.target.value))}
        >
          {PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

/** The signature pill-in-pill switcher, used wherever a view has modes. */
export function PillGroup<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: ReactNode }[];
  onChange: (value: T) => void;
}) {
  return (
    <div className="pill-group" role="tablist">
      {options.map((option) => (
        <button
          key={option.value}
          className="pill-tab"
          type="button"
          role="tab"
          aria-selected={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

// --- Form fields -----------------------------------------------------------

/**
 * Several controls under one label.
 *
 * `Field` is a `<label>`, which can only speak for a single control — pointing
 * one at a list of inputs makes every one of them the labelled control. This
 * renders the same label and hint over a group instead, so a group does not
 * have to reproduce the styling by hand to avoid the wrong element.
 */
export function FieldSet({
  label,
  hint,
  tip,
  children,
  className,
}: {
  label: string;
  hint?: string;
  /**
   * The same explanation as `hint`, behind the label instead of under it.
   *
   * For detail a reader wants **once** — what a control is for, what the
   * optional half of it means — where a permanent two-line paragraph above
   * every control is what makes a form look like documentation. The icon is the
   * affordance, and that is not decoration: a label with no marker gives nobody
   * a reason to hover it, so the hint may as well not exist. `HintedHeading`
   * states the same rule for a table column, and this is its label-shaped twin.
   *
   * **Not for anything a reader must see before pressing the button.** A
   * warning behind a hover is a warning for people who happened to hover, so a
   * consequence — this edit re-ranks every source bound to the statistic —
   * stays in `hint`, visible.
   *
   * Both may be given: the tip carries the explanation and the hint keeps
   * whatever has to stay on screen.
   */
  tip?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx("block", className)}>
      <span className="mb-xxs block text-body-sm-medium text-ink">
        {tip ? (
          <Tooltip hint={tip} className="cursor-help">
            {label}
            <InfoIcon size={12} className="shrink-0 text-ink-subtle" aria-hidden="true" />
          </Tooltip>
        ) : (
          label
        )}
      </span>
      {hint && <span className="mb-xs block text-caption text-muted-soft">{hint}</span>}
      {children}
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
  className,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={cx("block", className)}>
      <span className="mb-xxs block text-body-sm-medium text-ink">{label}</span>
      {children}
      {hint && !error && <span className="mt-xxs block text-caption text-muted-soft">{hint}</span>}
      {error && <span className="mt-xxs block text-caption text-danger-ink">{error}</span>}
    </label>
  );
}

/** Forwards its ref, so a form that rejects a value can focus the control. */
export const TextInput = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  function TextInput({ className, ...props }, ref) {
    return <input ref={ref} className={cx("input", className)} {...props} />;
  },
);

/** The in-row variant of `TextInput`: shorter, to sit beside a `SelectCompact`. */
export const TextInputCompact = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { width?: string }
>(function TextInputCompact({ className, width = "w-full", ...props }, ref) {
  // Width is a prop rather than something layered on through `className`,
  // because `.input` already sets one and two width utilities on an element
  // resolve by stylesheet order rather than by which was written last.
  return <input ref={ref} className={cx("input input-sm", width, className)} {...props} />;
});

export function Select({ className, ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={cx("select", className)} {...props} />;
}

/** The in-table variant: shorter, so a row of them does not tower. */
export function SelectCompact({ className, ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={cx("select select-sm", className)} {...props} />;
}

export function TextArea({ className, ...props }: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cx("input", className)} {...props} />;
}

/**
 * A checkbox, optionally in the mixed state.
 *
 * `indeterminate` is a DOM property with no HTML attribute behind it, so React
 * cannot set it from JSX and it has to be written to the node by hand. The
 * styling still comes from the stylesheet — CSS has `:indeterminate`, so the
 * mixed look needs no extra class.
 */
export function Checkbox({
  className,
  indeterminate = false,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { indeterminate?: boolean }) {
  const box = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (box.current) box.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return <input ref={box} type="checkbox" className={cx("checkbox", className)} {...props} />;
}

// --- Badges ----------------------------------------------------------------

/**
 * Everything a badge shares *except* its padding.
 *
 * The padding is left to each caller because Tailwind resolves two utilities
 * for the same property by their order in the generated stylesheet, not by
 * their order in the class string — so a base `px-sm` and an override
 * `px-[10px]` on the same element is a coin toss rather than an override.
 */
const BADGE_BASE =
  "inline-flex items-center gap-xxs whitespace-nowrap rounded-pill border text-caption leading-[1.3]";

/** The status pill's padding, and the tighter one the plain badge uses. */
const BADGE_PADDING = "px-sm py-xxs";
const BADGE_PADDING_TIGHT = "px-[10px] py-[2px]";

/**
 * Run status, encoded by shape *and* colour rather than colour alone: the pill
 * for a failure differs in outline and label from the pill for a success, so
 * the dashboard is readable without relying on hue.
 *
 * A live Run gets the one dark pill on the screen. `DESIGN.md` keeps dark
 * surfaces scarce, and "this is happening right now" is exactly the kind of
 * scarce signal that inversion is for.
 */
export function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case "succeeded":
      return (
        <span className={cx(BADGE_BASE, BADGE_PADDING, "border-success-line bg-success-soft text-success-ink")}>
          <Dot className="bg-success-ink" />
          Succeeded
        </span>
      );
    case "running":
      return (
        <span className={cx(BADGE_BASE, BADGE_PADDING, "border-transparent bg-surface-dark text-on-dark")}>
          <Dot className="animate-pulse-soft bg-badge-emerald" />
          Running
        </span>
      );
    case "queued":
      return (
        <span className={cx(BADGE_BASE, BADGE_PADDING, "border-hairline bg-surface-card text-ink")}>
          <Dot className="animate-pulse-soft bg-muted-soft" />
          Queued
        </span>
      );
    case "failed":
      return (
        <span className={cx(BADGE_BASE, BADGE_PADDING, "border-danger-line bg-danger-soft text-danger-ink")}>
          <AlertIcon size={12} />
          Failed
        </span>
      );
    case "skipped":
      return (
        <span className={cx(BADGE_BASE, BADGE_PADDING, "border-hairline bg-surface-soft text-muted")}>— Skipped</span>
      );
    case "cancelled":
      return (
        <span className={cx(BADGE_BASE, BADGE_PADDING, "border-hairline bg-surface-soft text-muted")}>⊘ Cancelled</span>
      );
    default:
      return (
        <span className={cx(BADGE_BASE, BADGE_PADDING, "border-hairline bg-canvas text-muted-soft")}>Never run</span>
      );
  }
}

function Dot({ className }: { className?: string }) {
  return <span className={cx("size-[6px] shrink-0 rounded-full", className)} />;
}

const BADGE_TONES = {
  neutral: "border-transparent bg-surface-card text-body",
  accent: "border-hairline bg-canvas text-ink",
  success: "border-success-line bg-success-soft text-success-ink",
  warning: "border-warning-line bg-warning-soft text-warning-ink",
  danger: "border-danger-line bg-danger-soft text-danger-ink",
} as const;

export function Badge({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: keyof typeof BADGE_TONES;
}) {
  return (
    <span className={cx(BADGE_BASE, BADGE_PADDING_TIGHT, BADGE_TONES[tone])}>{children}</span>
  );
}

/** Circular, 36px, pastel fill with initials. The one place pastels appear. */
const AVATAR_PALETTE = [
  "bg-badge-orange",
  "bg-badge-pink",
  "bg-badge-violet",
  "bg-badge-emerald",
] as const;

export function Avatar({
  name,
  size = "size-avatar",
  className,
}: {
  name: string;
  /** The size utility to draw at. A prop rather than something a caller layers
   *  on through `className`, because two size utilities on one element resolve
   *  by stylesheet order rather than by which was written last. */
  size?: string;
  className?: string;
}) {
  const seed = [...name].reduce((total, character) => total + character.charCodeAt(0), 0);
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  return (
    <span
      aria-hidden="true"
      className={cx(
        "inline-flex shrink-0 items-center justify-center rounded-full text-caption font-semibold text-on-dark",
        size,
        AVATAR_PALETTE[seed % AVATAR_PALETTE.length],
        className,
      )}
    >
      {initials || "?"}
    </span>
  );
}

// --- Messages --------------------------------------------------------------

const BANNER_TONES = {
  danger: { className: "border-danger-line bg-danger-soft text-danger-ink", Icon: AlertIcon },
  success: { className: "border-success-line bg-success-soft text-success-ink", Icon: CheckIcon },
  warning: { className: "border-warning-line bg-warning-soft text-warning-ink", Icon: AlertIcon },
  info: { className: "border-info-line bg-info-soft text-info-ink", Icon: InfoIcon },
} as const;

export function Banner({
  tone,
  title,
  children,
  className,
}: {
  tone: keyof typeof BANNER_TONES;
  title: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  const { className: toneClassName, Icon } = BANNER_TONES[tone];

  return (
    <div
      role="status"
      className={cx(
        "flex animate-rise gap-sm rounded-lg border px-md py-sm",
        toneClassName,
        className,
      )}
    >
      <Icon size={18} className="mt-px shrink-0" />
      <div className="min-w-0">
        <strong className="block text-body-sm-medium">{title}</strong>
        {children && <div className="mt-xxs text-body-sm opacity-90">{children}</div>}
      </div>
    </div>
  );
}

/**
 * What a fresh account lands on. A light-gray card rather than a colour block:
 * an empty state is not an error, and should not read as one.
 */
export function EmptyState({
  title,
  children,
  action,
  icon,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="rounded-lg bg-surface-card px-xl py-xxl text-center">
      {icon && (
        <div className="mx-auto mb-md flex size-control items-center justify-center rounded-lg border border-hairline bg-canvas text-ink">
          {icon}
        </div>
      )}
      <h2 className="m-0 font-display text-display-sm">{title}</h2>
      <div
        className={cx(
          "mx-auto mt-sm max-w-[56ch] text-body-md text-muted",
          action ? "mb-lg" : "mb-0",
        )}
      >
        {children}
      </div>
      {action}
    </div>
  );
}

export function Spinner({ label = "Loading" }: { label?: string }) {
  return (
    <div role="status" className="flex items-center gap-sm p-xl text-body-sm text-muted">
      <span className="spinner" />
      {label}…
    </div>
  );
}

/**
 * A table that scrolls inside its own box rather than widening the page.
 *
 * **`overflow-x-auto` makes this a scroll container on *both* axes**, which is
 * not what the class list says and is the fact every feature touching this box
 * has had to work around. CSS computes `overflow-y: visible` to `auto` the
 * moment the other axis is not visible, so the box clips vertically too. It is
 * why `usePlacement` draws its menus `fixed` — an absolutely positioned one
 * left 392 statistics as a sliver of a line — and it is why `stickyHeader`
 * below cannot simply be a CSS rule.
 */
export function TableScroll({
  children,
  /**
   * Keep the column headings on screen while the page scrolls past the rows.
   *
   * **Opt-in, because it cannot always be honoured.** `position: sticky` sticks
   * inside the nearest scrolling ancestor, and that is this box rather than the
   * page — measured, this box's `scrollHeight` equals its `clientHeight`, so it
   * never scrolls vertically and a sticky heading inside it never moves.
   * Driven in a browser on the Fields table: a plain
   * `thead th { position: sticky; top: 0 }` left the heading at **-792px**
   * after a 1200px scroll, which is to say it did nothing at all.
   *
   * So the clipping is dropped instead — but **only once it has been measured
   * to be unnecessary**, which is the whole of the care here. The table really
   * does overflow at narrow widths: the Fields table is 1063px of content in
   * 770px of box at a 1100px window, against 1134 in 1134 at 1600. Removing
   * the clipping on a table that needs it would spill the rows out of the card,
   * which is worse than a heading that scrolls away.
   *
   * **A table that must keep its scroller sticks to this box instead, and that
   * is the third state.** Leaving the heading alone there was the first shape of
   * this and it made the prop a promise it did not keep: the Stat Dictionary at
   * a zoomed-in window is eight columns wide enough to break `accurate_crosses`
   * across two lines, so it overflows, so it got no sticky heading at all and
   * nothing said why. There is no CSS out of that — `overflow-x: auto` with
   * `overflow-y: visible` computes the second to `auto`, and `clip` computes to
   * `hidden`, so a horizontal scroller is a vertical scroll container whatever
   * is asked for. What is left is to make that container one the heading can
   * usefully stick to: capped at a screen and scrolling vertically, the heading
   * pins to its own top edge, and the page scrolls to put that edge under the
   * topbar. The cost is a scrollbar inside the card, which is worth paying for
   * a heading that is actually there.
   */
  stickyHeader = false,
}: {
  children: ReactNode;
  stickyHeader?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  // Clipped until measured. The safe state first, so a table that does overflow
  // is never briefly painted spilling out of its card.
  const [overflowing, setOverflowing] = useState(true);

  useLayoutEffect(() => {
    if (!stickyHeader) return;
    const el = box.current;
    if (!el) return;

    const measure = () => setOverflowing(el.scrollWidth > el.clientWidth);
    measure();

    // The box and the table both: the window resizing changes the first, and a
    // queue pill filtering the rows changes the second — a Statistic column
    // sized to its widest name is a different width with 188 rows than with
    // 472.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => observer.disconnect();
  }, [stickyHeader]);

  // Which of the two scrollports the heading can stick to, if either. Never
  // both at once, and the distinction is the whole of this component: one needs
  // the clipping gone and the other needs it kept.
  const pageSticky = stickyHeader && !overflowing;
  const boxSticky = stickyHeader && overflowing;
  return (
    <div
      ref={box}
      className={cx(
        "w-full",
        // `overflow-visible` is what lets a sticky heading reach the page's
        // scrollport, and it is only ever set with nothing to scroll.
        // `overflow-auto` is the other direction: the box keeps the horizontal
        // scroller it needs and takes a vertical one, which is what gives the
        // heading a scrollport of its own to stick to.
        pageSticky ? "overflow-visible" : boxSticky ? "table-capped overflow-auto" : "overflow-x-auto",
        // **Never `table-sticky-head` on a box that is still a scroll
        // container.** A sticky heading there does not fail quietly: `top` is a
        // minimum distance from the scrollport's edge, so at scroll nought it
        // *pushes the heading down* by that much instead of leaving it alone.
        // Shipped that way round for one commit, and the ESPN NFL table — which
        // overflows by four pixels, 1138 into 1134 — drew its heading 64px
        // below the thead's own box and on top of the first row, with nothing
        // scrolled. The `-panel` rule is the one with `top: 0`, which is the
        // right distance when the scrollport is this box.
        pageSticky && "table-sticky-head",
        boxSticky && "table-sticky-head-panel",
      )}
    >
      {children}
    </div>
  );
}

/**
 * An explanation attached to something, shown the moment it is pointed at.
 *
 * **Replaces the `title` attribute, which was measured at three to four
 * seconds before appearing.** That delay is set by the browser and cannot be
 * configured, which makes `title` the wrong carrier for a hint whose whole job
 * is to be found: by the time it arrives the reader has moved on, and the
 * control is back to explaining nothing.
 *
 * **`position: fixed`, measured on open.** The field tables live inside
 * `TableScroll`'s `overflow-x-auto`, which clips an absolutely-positioned
 * child — the hint on the last column would be cut off by the very container
 * that makes the table readable. Fixed coordinates escape the scroller, and
 * are taken from the trigger's own rect at the moment it opens so they are
 * right wherever the table has been scrolled to.
 *
 * **Keyboard and pointer both open it**, because a hint only a mouse can reach
 * is a hint half the ways into this screen cannot. Escape closes it, which is
 * what a reader who opened it by tabbing will try.
 *
 * The bubble stays in the DOM while closed, hidden, so `aria-describedby`
 * always points at something real — a reference to a node that does not exist
 * is announced as nothing at all.
 */
export function Tooltip({
  hint,
  children,
  className,
}: {
  hint: string;
  children: ReactNode;
  className?: string;
}) {
  const id = useId();
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const trigger = useRef<HTMLSpanElement>(null);

  /** Where the bubble sits: under the trigger, nudged back inside the window. */
  function open() {
    const rect = trigger.current?.getBoundingClientRect();
    if (!rect) return;
    // The bubble is at most `WIDTH` wide, so this is what keeps a hint on the
    // right-hand column from running off the edge of the window.
    const WIDTH = 320;
    const MARGIN = 8;
    const left = Math.min(rect.left, window.innerWidth - WIDTH - MARGIN);
    setAt({ top: rect.bottom + 6, left: Math.max(MARGIN, left) });
  }

  useEffect(() => {
    if (!at) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") setAt(null);
    };
    window.addEventListener("keydown", close);
    // Closed on scroll rather than followed: the coordinates were measured
    // once, and a bubble that stays put while its trigger moves is worse than
    // one that goes away.
    window.addEventListener("scroll", () => setAt(null), { once: true, capture: true });
    return () => window.removeEventListener("keydown", close);
  }, [at]);

  return (
    <span
      ref={trigger}
      // `gap-xxs` and not `gap-2xs`. The scale is xxs/xs/sm/md/lg/xl/xxl, so
      // `2xs` named nothing, generated no CSS, and left the info icon flush
      // against its label on every tooltip in the portal — the hinted column
      // headings included. Reported against the position-group dialog.
      className={cx("relative inline-flex items-center gap-xxs", className)}
      tabIndex={0}
      aria-describedby={id}
      onMouseEnter={open}
      onMouseLeave={() => setAt(null)}
      onFocus={open}
      onBlur={() => setAt(null)}
    >
      {children}
      <span
        id={id}
        role="tooltip"
        className={cx(
          "fixed z-50 w-[20rem] rounded-md border border-line bg-canvas p-sm text-left",
          // `whitespace-normal` is load-bearing, not tidiness. A table heading
          // carries `whitespace-nowrap`, which this inherits — measured, the
          // hint rendered as a single 313-character line running off the side
          // of the window. The rest resets what a `<th>`'s own type styling
          // would otherwise impose on a sentence.
          "whitespace-normal text-body-sm font-normal normal-case tracking-normal",
          "text-ink shadow-lg",
          at ? "block" : "hidden",
        )}
        style={at ? { top: at.top, left: at.left } : undefined}
      >
        {hint}
      </span>
    </span>
  );
}

/**
 * A long list kept inside its own frame, scrolling on its own.
 *
 * `TableScroll`'s vertical counterpart, and it exists for one measured reason:
 * a Wyscout Source Profile holds **175 Feeds**, each printing a four-hundred
 * character address, so the Feeds list alone ran to dozens of screens. Anything
 * below it on the page — the field review link, the Credential, the Schedule,
 * the Run history — was unreachable without scrolling past all of them.
 *
 * Framed rather than merely clipped. A scrollable region with no border reads
 * as content that simply stops, so somebody looking for the twentieth Feed
 * concludes there are nineteen. The border and the inner padding are what say
 * "there is more in here".
 *
 * `max` is a Tailwind max-height class so a caller can size the panel to its
 * content — a list of ten Feeds does not want the height a list of a hundred
 * and fifty needs — and the default suits a list long enough to need this at
 * all. Nothing scrolls when the content is shorter than the cap, so passing a
 * short list through here is harmless.
 */
export function ScrollPanel({
  children,
  max = "max-h-[18rem]",
}: {
  children: ReactNode;
  max?: string;
}) {
  return (
    <div
      className={cx(
        "w-full overflow-y-auto overscroll-contain rounded-md border border-hairline bg-surface-soft p-md",
        max,
      )}
      // Reachable by keyboard, which an overflow container is not by default:
      // a person who cannot use a wheel or a trackpad has no other way into
      // the hundred and fifty-fourth Feed.
      tabIndex={0}
    >
      {children}
    </div>
  );
}

// --- Modal -----------------------------------------------------------------

/**
 * A card lifted off the page, for content that answers a question rather than
 * being part of the form.
 *
 * `ScrollPanel`'s sibling and its escalation. A panel keeps a long list from
 * pushing the rest of a screen out of reach; a modal is for a list that has no
 * business being on the screen at all until it is asked for. The storage path
 * preview is the case that produced this: nine paths per source, times every
 * source in the Workspace, sitting under the destination form and burying
 * everything below it — and nobody reading it while they type a bucket name.
 *
 * Three things a hand-rolled overlay usually gets wrong and this does not:
 * Escape closes it, the scrim closes it, and the page behind it does not
 * scroll while it is open. The panel itself scrolls, so a long body is
 * reachable without the modal growing past the viewport.
 *
 * Not `<dialog>`. Its top-layer rendering ignores the stacking context and its
 * `::backdrop` cannot be styled from the theme tokens, so a modal would be the
 * one surface in the portal whose scrim came from somewhere other than
 * `styles.css`.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  width = "max-w-[64rem]",
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** A Tailwind max-width, so a caller can size the panel to its content. */
  width?: string;
}) {
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);

    // Restored to whatever it was rather than cleared, so a screen that sets
    // its own overflow is not quietly undone by closing a modal.
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // The close button would do, but the panel is what the reader wants
    // scrolled, and an unfocused overflow container does not answer the arrow
    // keys.
    panel.current?.focus();

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-md fold:p-xl">
      {/* A button rather than a div: closing by clicking away is an action, and
          it should be reachable without a pointer. */}
      <button
        type="button"
        className="fixed inset-0 cursor-default border-none bg-surface-dark opacity-40"
        aria-label="Close"
        onClick={onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        className={cx(
          "relative my-auto flex max-h-[85vh] w-full flex-col",
          "overflow-hidden rounded-lg border border-hairline bg-canvas shadow-overlay",
          width,
        )}
      >
        <header className="flex shrink-0 items-start justify-between gap-md border-b border-hairline px-lg py-md">
          <div className="min-w-0">
            <h2 className="m-0 font-text text-title-sm">{title}</h2>
            {description && <p className="mb-0 mt-xxs text-body-sm text-muted">{description}</p>}
          </div>
          <IconButton
            icon={<CloseIcon size={16} />}
            label="Close"
            onClick={onClose}
            className="shrink-0"
          />
        </header>

        <div
          ref={panel}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-lg py-md"
          tabIndex={-1}
        >
          {children}
        </div>

        {footer && (
          <footer className="flex shrink-0 flex-wrap items-center justify-end gap-xs border-t border-hairline px-lg py-md">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}
